import * as moment from 'moment';
import { AccountPreferences } from './accounts';
import { Company } from './companies';
import { DeadlineContract } from './deadlines-contracts';
import { Equipment } from './equipments';
import { Group } from './groups';
import { LocationGroup } from './locations';
import { LocationEnvironment } from './locations-environments';
import { LocationSector } from './locations-sectors';
import { MaintenanceType } from './maintenance-type';
import { MaintenanceDeadlineContract, MaintenanceDeadlineStatusEnum } from './maintenances';
import { Problem } from './problems';
import { ProductService, Quotation } from './quotations';
import { Segment } from './segments';
import { Task, TaskEmployee, TaskLog, TaskOrMaintenanceLogUpdatedBySourceEnum } from './tasks';
import { User } from './users';
export * from './accounts';
export * from './approvals-equipments';
export * from './companies';
export * from './equipments';
export * from './groups';
export * from './images';
export * from './imports';
export * from './integrations';
export * from './locations';
export * from './maintenance-type';
export * from './maintenances';
export * from './pendency-types';
export * from './problems';
export * from './quotations';
export * from './recurrences';
export * from './reports/deadlines';
export * from './reports/forms-answers';
export * from './segments';
export * from './tasks';
export * from './upload-credentials';
export * from './users';

export type MaintenanceStatus = 'new' | 'pending' | 'done' | 'scheduled' | 'inProgress' | 'canceled' | 'reported' | 'requested';

export interface FormDialogData {
  form: Form;
  isViewMode: boolean;
  questionsLimit: number;
  questionOptionsLimit: number;
  showScoreOptions: boolean;
}

export enum MaintenanceTypeEnum {
  'preventiveMaintenance',
  'correctiveMaintenance'
}

export enum TimelineIntervalEnum {
  'lastSevenDays',
  'lastThirtyDays',
  'lastNinetyDays',
  'lastSixMonths',
  'lastTwelveMonths'
}

export enum QuestionTypeEnum {
  uniqueCheckbox = 'uniqueCheckbox',
  signature = 'signature',
  picture = 'picture',
  shortAnswer = 'shortAnswer',
  paragraph = 'paragraph',
  dropdown = 'dropdown',
  multipleChoice = 'multipleChoice',
  multiplePicture = 'multiplePicture',
  checkboxes = 'checkboxes',
  numeric = 'numeric',
  date = 'date',
  currency = 'currency',
  hour = 'hour',
  email = 'email',
  phone = 'phone',
  document = 'document'
}

export enum ServiceProviderOrderByEnum {
  segmentName_asc = 'segmentName_asc',
  segmentName_desc = 'segmentName_desc',
  createdAt_asc = 'createdAt_asc',
  createdAt_des = 'createdAt_des'
}

export interface ExtraChartData {
  openedAtMin: string;
  openedAtMax: string;
}

export interface MaintenanceChartDataItem {
  name: string;
  value: number;
  extra?: ExtraChartData;
}

export interface MaintenanceFilter {
  assigneeId_in?: string[];
  number_contains?: string;
  message_contains?: string;
  requesterId_in?: string[];
  status_in?: MaintenanceStatus[] | string[];
  segmentId_in?: string[];
  pendenciesStatus_in?: PendencyStatus[];
  archived?: boolean;
  has_equipments?: boolean;
  typeId_in?: string[];
  locationId_in?: string | string[];
  equipmentId_in?: string | string[];
  locationGroupId_in?: string | string[];
  labelId_in?: string | string[];
  rating_in?: string | string[];
  openedAt_gte?: Date | string;
  openedAt_lte?: Date | string;
  closedAt_gte?: Date | string;
  closedAt_lte?: Date | string;
  scheduledAt_gte?: Date | string;
  scheduledAt_lte?: Date | string;
  locationsGroupsId_in?: string | string[];
  companyId_in?: string[];
  problemId_in?: string[];
  pendenciesTreatmentStatus_in?: PendencyTreatmentStatus[];
  firstVisitStartedOnPeriod?: boolean;
  closedOnPeriod?: boolean;
  responseDeadlineStatus_in?: MaintenanceDeadlineStatusEnum[];
  solutionDeadlineStatus_in?: MaintenanceDeadlineStatusEnum[];
  warrantyMaintenanceStatus?: boolean;
  warrantyStartDateLte?: Date | string;
  warrantyEndDateGte?: Date | string;
  warrantyStatusIn?: string[];
  pendenciesTypeId_in?: string[];
  paymentStatus_in?: string[];
  paymentNumber_contains?: string;
}

export interface StorageMovementFilter {
  from?: Date | string;
  to?: Date | string;
  productId?: string;
  storageId_in?: string[];
}

export interface VolumetryFilter {
  from?: Date | string;
  to?: Date | string;
  type_in?: MaintenanceTypeEnum[];
  typeId_in?: string[];
  locationGroupId_in?: string | string[];
  locationId_in?: string | string[];
  equipmentId_in?: string | string[];
  segmentId_in?: string[];
  companyId_in?: string[];
  scheduled_at_gte?: Date | string;
  scheduledAt_gte?: Date | string;
  scheduled_at_lte?: Date | string;
  scheduledAt_lte?: Date | string;
  closedAt_gte?: Date | string;
  closedAt_lte?: Date | string;
}

export interface ListMaintenancesResult {
  maintenances?: PagedResult<Maintenance>;
  employeesPositions?: EmployeesPosition[];
  filtered?: boolean;
}

export interface EmployeesPosition {
  id: string;
  company: Company;
  employee: TaskEmployee;
  coords: {
    latitude: number;
    longitude: number;
  };
  maintenanceHistoric?: MaintenanceHistoric;
}

export interface MaintenanceHistoric {
  last?: Maintenance;
  current?: Maintenance;
  next?: Maintenance;
}

export interface PagedResult<T = any> {
  items?: T[];
  pageInfo?: {
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    startCursor?: string;
    endCursor?: string;
  };
  totalCount?: number;
  filtered?: boolean;
}

export interface ExportVariables {
  where: any;
  timezone: string;
}

export interface QueryOptions {
  limit: number;
  offset: number;
  orderBy: string;
}

export interface MaintenanceProductService {
  value: number;
  quantity: number;
  storage: MaintenanceProductStorage;
  item: ProductService;
}

export interface MaintenanceProductStorage {
  id: string;
  name: string;
}

export interface MaintenanceProductServicePageResult extends PagedResult<MaintenanceProductService> {
  totalValue: number;
}

export interface Maintenance {
  id: string;
  message: string;
  address: Address;
  statusDescription: string;
  comments: Comment[];
  archived: Boolean;
  assignee: User;
  attachments: Attachment[];
  equipments: Equipment[];
  tasks: Task[];
  company: Company;
  labels: Label[];
  openedAt: string;
  location: Location;
  locationSectors: LocationSector[];
  locationEnvironments: LocationEnvironment[];
  number: string;
  requestedAt: string;
  closedAt: string;
  deadlineAt: string;
  firstVisitStartedAt: string;
  firstVisitDeadlineAt: string;
  requester: User;
  status: MaintenanceStatus | string;
  subject: string;
  type: MaintenanceTypeEnum | string;
  maintenanceType: MaintenanceType;
  segment: Segment;
  problem: Problem;
  logs: MaintenanceLog[];
  soUrl: string;
  visitCount?: number;
  productsServices?: MaintenanceProductServicePageResult;
  formAnswers: FormAnswers[];
  quotations: Quotation[];
  scheduleSuggestions?: ScheduleDatetime[];
  scheduleSuggestionsStatus?: string;
  scheduledAt?: string;
  scheduled?: ScheduleDatetime;
  companyScheduleSuggestion?: ScheduleDatetime;
  responseDeadlineStatus?: MaintenanceDeadlineStatusEnum;
  solutionDeadlineStatus?: MaintenanceDeadlineStatusEnum;
  deadlineContract?: MaintenanceDeadlineContract;
  totalValue: number;
  productsTotalValue: number;
  servicesTotalValue: number;
  previousMaintenance: Maintenance;
  weight: number;
  pendencies: Pendency[];
}

interface ScheduleDatetime {
  date: any;
  time: any;
}

export interface FormAnswers {
  id: string;
  name: string;
  archived: Boolean;
  createdAt: string;
  questionAnswers: QuestionAnswer;
  equipment: Equipment;
  type: string;
}

export interface QuestionAnswer {
  id: string;
  title: string;
  answer: string;
  type: QuestionTypeEnum;
  position: string;
  required: Boolean;
  options: Option[];
  conditions: Conditions[];
  createdAt: string;
  answeredAt: string;
}

export interface MaintenanceLog {
  clientApplicationName?: TaskOrMaintenanceLogUpdatedBySourceEnum;
  clientSource?: 'browser' | 'mobile';
  assignee?: User;
  closedAt?: string;
  company?: Company;
  createdAt?: string;
  deadlineAt?: string;
  location?: Location;
  maintenanceType?: MaintenanceType;
  message?: string;
  number?: string;
  requester?: User;
  archived?: boolean;
  scheduleSuggestions?: ScheduleDatetime[];
  scheduleSuggestionsStatus?: string;
  segment?: Segment;
  status?: MaintenanceStatus;
  statusDescription?: string;
  type?: MaintenanceTypeEnum;
  updatedAt?: string;
  relocationReason?: string;
  updatedBy?: {
    id?: string;
    name?: string;
    type?: string;
  };
}

export interface MaintenanceAndTasksLogsMapped extends MaintenanceLog, TaskLog {
  icon: string;
  image?: string;
  eventName: string;
  colorBackground: string;
  typeLog: string;
  customAuthor?: string;
  customAuthorDevice?: string;
  dateCreatedAt: string;
  dateTimeCreatedAt: string;
  eventDescription?: string;
  eventNote?: string;
  isCreatedLog?: boolean;
  createdLogBy?: string;
  isRateLog?: boolean;
  showMessage?: boolean;
}

export interface MaintenanceLogSelectOption {
  title: string;
  value: number | string;
  assignee?: User;
  closedAt?: string;
  company?: Company;
  createdAt?: string;
  deadlineAt?: string;
  location?: Location;
  message?: string;
  number?: string;
  requester?: User;
  segment?: Segment;
  status?: MaintenanceStatus;
  type?: MaintenanceTypeEnum;
  updatedAt?: string;
  relocationReason?: string;
  updatedBy?: User;
  clientIpAddress?: string;
  updatedByName?: string;
  clientApplicationName?: string;
}

export interface Label {
  id: string;
  name: string;
  color: string;
  archived: boolean;
  type?: string;
}

export interface Form {
  id?: string;
  name?: string;
  archived?: boolean;
  createdAt?: string;
  questions?: Question[];
  logs?: FormLog[];
}

export interface ExportForm extends Omit<Form, 'questions'> {
  id?: string;
  name?: string;
  type?: string;
  questionsCount?: number;
  archived?: boolean;
  createdAt?: string;
  questions?: ExportFormQuestion[];
  logs?: FormLog[];
  hash?: string;
}

export interface ExportFormQuestion extends Omit <Question, 'type' | 'conditions' | 'options'> {
  type: string;
  oldId: string;
  conditions?: ExportFormQuestionCondition[];
  options: ExportFormQuestionOption[];
}

export interface ExportFormQuestionOption {
  oldId: string;
  id: string;
}

export interface ExportFormQuestionCondition extends Conditions {
  questionId: string;
  oldId: string;
  questionTitle: string;
}

export interface Question {
  id: string;
  title: string;
  position: number;
  required: boolean;
  type: QuestionTypeEnum;
  options?: Option[];
  conditions?: Conditions[];
  configuration?: QuestionConfiguration;
}

export interface QuestionConfiguration {
  validationRegexp: string;
  validationMessage: string;
  maskRegexp: string;
  mask: string;
  exampleValue: string;
  transform: string;
  maxLength?: number;
}

export interface Conditions {
  value: string;
  operator: string;
  question: Question;
}

export interface Option {
  id: string;
  value: string;
  position: number;
  score: number;
}

export interface FormLog {
  id?: string;
  createdAt: string;
  updatedBy?: User;
  updatedByName?: string;
  clientIpAddress?: string;
  clientApplicationName?: string;
  snapshot?: FormSnapshot;
}

export interface FormSnapshot {
  id?: string;
  name: string;
  archived: boolean;
  questions?: Question[];
}

export interface FormLogMapped extends FormLog {
  icon: string;
  eventName: string;
  colorClass: string;
  eventDescription: string;
  formattedCreatedAt: string;
}

export interface Comment {
  id: string;
  createdAt: string;
  message?: string;
  postedBy?: Author;
  type: 'internal' | 'company' | 'collaborator';
  archived: boolean;
  maintenance?: {
    id: string
  };
  company?: Company;
}

export interface Author {
  id: string;
  name: string;
  avatarUrl: string;
  type: 'user' | 'company';
}

export interface Attachment {
  createdAt: string;
  id: string;
  path: string;
  type: 'request' | 'integration';
  size: number;
  title: string;
}

export interface Location {
  id?: string;
  name?: string;
  address?: Address;
  segments?: Segment[];
  group?: LocationGroup;
  serviceProviders?: LocationServiceProvider[];
  archived?: boolean;
  notes?: string;
  contact?: LocationContact;
  documentNumber?: string;
  groupsConnection?: PagedResult<Group>;
  serviceProvidersConnection?: PagedResult<LocationServiceProvider>;
}

export interface LocationDialogData {
  location: Location;
  isViewMode?: boolean;
}

export interface LocationDialogResult {
  reload?: boolean;
  location?: Location;
}

export interface LocationContact {
  emails?: LocationContactEmail[];
  phones?: LocationContactPhone[];
}

export interface LocationContactEmail {
  id?: string;
  contactType: 'personal' | 'work';
  emailAddress: string;
  primary: boolean;
}

export interface LocationContactPhone {
  id?: string;
  contactType: 'home' | 'work' | 'cell';
  number: string;
  primary: boolean;
}

export interface Address {
  city: string;
  complement: string;
  formattedAddress?: string;
  neighborhood: string;
  number: string;
  postalCode: string;
  state: string;
  streetName: string;
  street?: string;
  coords: AddressCoords;
}

export interface AddressCoords {
  latitude: number;
  longitude: number;
}

export interface LocationServiceProvider {
  company: Company;
  segment: Segment;
  createdAt?: string;
  deadlineContract?: DeadlineContract;
  maintenancesTypes?: MaintenanceType[];
}

export interface LocationInput {
  name: string;
  address: Address;
  group: string;
  segments: string;
  serviceProviders?: LocationServiceProviderInput[];
  contact: LocationContact;
  archived: boolean;
  notes: string;
  documentNumber: string;
}

export interface ServiceProviderConnectionInput {
  locationId: string;
  limit?: number;
  offset: number;
  orderBy: ServiceProviderOrderByEnum;
}

export interface LocationServiceProviderInput {
  segmentId: string;
  companyId: string;
  deadlineContractId?: string;
  maintenanceTypesId?: string[];
}

export interface LocationPermissionInput {
  groupsId?: string[];
  segmentId?: string;
}

export interface Tokens {
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
}

export interface CognitoResponse {
  challengeName?: string;
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  session?: string;
  resetPasswordSession?: string;
  passwordStatus?: {
    daysToExpires?: number;
    isPasswordExpired?: boolean;
    isPasswordSoonExpired?: boolean;
  };
}

export interface Permission {
  id: string;
  name: string;
  checked?: boolean;
  category: string;
  visible?: boolean;
  featureFlag?: string;
  translation?: {
    pt: {
      title: string;
      description: string;
    },
    en: {
      title: string;
      description: string;
    }
    es: {
      title: string;
      description: string;
    }
  };
}

export interface PermissionCategorie {
  id: string;
  category: string;
  translation?: {
    pt: {
      title: string;
      description: string;
    },
    en: {
      title: string;
      description: string;
    }
    es: {
      title: string;
      description: string;
    }
  };
  permissions: Permission[];
}

export interface Atribute {
  Name: 'custom:account_id' | 'custom:user_id' | 'custom:user_type' | 'email' | 'email_verified' | 'name' | 'sub';
  Value: string;
}

export interface LogTimeLineItem {
  createdAt: string;
  icon: string;
  title: string;
  subtitle: string;
  content: string;
  updatedBy?: User;
  status: string;
  rating?: Number;
  stars?: string[];
  button?: {
    text: string;
    action(): void;
  };
  emotion?: string;
}

export interface Account {
  id: string;
  name: string;
  createdAt: string;
  preferences: AccountPreferences;
  featureFlags?: AccountFeatureFlag[];
}

export interface AccountFeatureFlag {
  name: AccountFeatureFlagNameEnum;
  active: boolean;
}

export enum AccountFeatureFlagNameEnum {
  storage = 'storage'
}

export interface MomentDuration {
  amount: moment.DurationInputArg1;
  unit: moment.DurationInputArg2;
}

export interface Interval {
  name: string;
  value: string;
  id: number;
}
export interface LoginCustomization {
  logoUrl?: string;
  accountName?: string;
  color?: {
    class?: string;
    background?: string;
    foreground?: string;
  };
  description?: string;
  clientId?: string;
  tenantId?: string;
  openIdConfigurationEndpoint?: string;
}

export interface Pendency {
  id: number;
  title: string;
  archived: boolean;
  treatmentStatus: PendencyTreatmentStatus;
  status: PendencyStatus;
  type: string;
}

export enum PendencyStatus {
  'opened',
  'done'
}

export enum PendencyTreatmentStatus {
  'company',
  'enterprise'
}
